// ============================================================================
// The event catalog — a compile error is the enforcement
// ============================================================================
//
// Every analytics layer eventually grows a second spelling of the same event
// (`ticket_created` and `ticket-create` and `createdTicket`), and by the time
// anybody notices, the reports have been quietly wrong for a quarter. The
// existing GA layer solved this with a discriminated union so a wrong event is
// a TYPE error; this is the same trick for the first-party store.
//
// What is deliberately NOT done: a database enum, or a `z.enum` over the event
// names baked at build time. `MARKETING_BRANDS` is the precedent — a frozen
// list means a migration (or a deploy) to add a screen, and the list stops
// being adopted. The column is `text`; the catalog is the contract; the compiler
// is the guard.
//
// A catalog is built ONCE per app and passed to `recordUsage`/`trackUsage`,
// which are generic over its names. An unregistered event does not compile.
// ============================================================================

import type { UsageActorKind } from "./types";

/** One registered event. `description` is not decoration — the admin console and Meridian render it. */
export interface UsageEventDefinition<Name extends string = string> {
  readonly name: Name;
  /** Default surface, used when a call site does not name one. */
  readonly surface: string;
  /** A sentence a non-engineer can read: "Opened their ticket list". */
  readonly description: string;
  /**
   * Who can plausibly produce this. Reporting only — it is never enforced at
   * write time, because a wrong guess here must not DROP a real event.
   */
  readonly actorKinds?: readonly UsageActorKind[];
}

export interface UsageCatalog<Name extends string> {
  readonly names: readonly Name[];
  readonly definitions: readonly UsageEventDefinition<Name>[];
  isKnownEvent(value: string): value is Name;
  describe(name: string): string;
  surfaceFor(name: Name): string;
}

/**
 * Event names are lower_snake_case and short.
 *
 * Checked at MODULE LOAD, not at write time: a malformed name is a programming
 * mistake that should stop the process that contains it, and discovering it on
 * the one request that happened to fire the event is how it reaches production.
 * Same posture as `assertReadOnly` on the PTC tool list.
 */
const NAME_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
const MAX_NAME_LENGTH = 48;

export function assertValidEventName(name: string): void {
  if (name.length > MAX_NAME_LENGTH) {
    throw new Error(
      `usage-analytics: event name "${name}" is ${name.length} characters; the limit is ${MAX_NAME_LENGTH}`,
    );
  }
  if (!NAME_PATTERN.test(name)) {
    throw new Error(
      `usage-analytics: event name "${name}" must be lower_snake_case (matched against ${String(NAME_PATTERN)})`,
    );
  }
}

/**
 * Build an app's catalog.
 *
 * `const` type parameter so the literal names survive into the type — that is
 * what makes `recordUsage(catalog, { event: "typo" })` fail to compile rather
 * than widening to `string` and passing.
 */
export function defineUsageEvents<const T extends readonly UsageEventDefinition[]>(
  definitions: T,
): UsageCatalog<T[number]["name"]> {
  type Name = T[number]["name"];
  const byName = new Map<string, UsageEventDefinition<Name>>();
  for (const definition of definitions) {
    assertValidEventName(definition.name);
    if (byName.has(definition.name)) {
      // A duplicate is not harmless: two definitions means two descriptions,
      // and whichever one the console renders is then a coin flip.
      throw new Error(`usage-analytics: duplicate event definition "${definition.name}"`);
    }
    byName.set(definition.name, definition as UsageEventDefinition<Name>);
  }
  const names = definitions.map((definition) => definition.name) as Name[];
  return {
    names,
    definitions: definitions as readonly UsageEventDefinition<Name>[],
    isKnownEvent(value: string): value is Name {
      return byName.has(value);
    },
    describe(name: string): string {
      // An unknown name still renders. Rows outlive catalogs — an event removed
      // from the code is still in the table, and hiding it would silently
      // shorten somebody's history.
      return byName.get(name)?.description ?? name.replace(/_/g, " ");
    },
    surfaceFor(name: Name): string {
      return byName.get(name)?.surface ?? "unknown";
    },
  };
}

/**
 * The events every WTC app produces, whatever it is.
 *
 * Kept small on purpose. An app's own events go in its own file and are
 * concatenated with these; nothing product-specific belongs here.
 */
export const CORE_USAGE_EVENTS = [
  {
    name: "signed_in",
    surface: "auth",
    description: "Signed in",
  },
  {
    name: "signed_out",
    surface: "auth",
    description: "Signed out",
  },
  {
    name: "session_start",
    surface: "app",
    description: "Started using the app",
  },
  {
    name: "page_view",
    surface: "app",
    description: "Opened a page",
  },
  {
    name: "search_performed",
    surface: "search",
    description: "Ran a search",
  },
  {
    name: "record_viewed",
    surface: "app",
    description: "Opened a record",
  },
  {
    name: "file_downloaded",
    surface: "files",
    description: "Downloaded a file",
  },
  {
    name: "report_exported",
    surface: "reports",
    description: "Exported a report",
  },
  {
    name: "feature_used",
    surface: "app",
    description: "Used a feature",
  },
] as const satisfies readonly UsageEventDefinition[];

export type CoreUsageEventName = (typeof CORE_USAGE_EVENTS)[number]["name"];
